import { useState, useEffect, useRef } from 'react'
import { type User, onAuthStateChanged } from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { usePostHog } from 'posthog-js/react'
import { Capacitor } from '@capacitor/core'
import { Posthog as PosthogCapacitor } from '@capawesome/capacitor-posthog'
import { setMetaUserIdentity } from '@/lib/metaEvents'

let lastIdentifiedUserId: string | null = null

export const useAuthState = () => {
  // Initialize with Firebase's cached auth state instead of null
  // auth.currentUser is synchronously available if user was previously logged in
  // This prevents the "log in to view" flicker by ensuring user is never null during initial render
  const [user, setUser] = useState<User | null>(() => auth.currentUser)
  const [loading, setLoading] = useState(true)
  const posthog = usePostHog()
  const unsubscribeRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    // Only set up the listener once per component mount
    if (unsubscribeRef.current) {
      return
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user)
      setLoading(false)
      
      // Identify user in PostHog when they sign in
      // Only call identify if the user ID has actually changed to prevent duplicate person property sets
      if (user && lastIdentifiedUserId !== user.uid) {
        lastIdentifiedUserId = user.uid
        
        if (posthog) {
          console.log('[PostHog] Identifying user and linking anonymous session:', user.uid)
          posthog.identify(user.uid, {
            email: user.email,
            name: user.displayName,
            photo_url: user.photoURL,
            email_verified: user.emailVerified,
            provider: user.providerData[0]?.providerId || 'email',
          })
        }
        
        // Also identify on native platform using Capacitor plugin
        if (Capacitor.isNativePlatform()) {
          console.log('[PostHog] Native: Identifying user via Capacitor plugin:', user.uid)
          PosthogCapacitor.identify({
            distinctId: user.uid,
            userProperties: {
              email: user.email,
              name: user.displayName,
              photo_url: user.photoURL,
              email_verified: user.emailVerified,
              provider: user.providerData[0]?.providerId || 'email',
            },
          })
        }

        // Set hashed user identity for Meta standard events (Lead, Purchase, etc.)
        // This runs on every login and app resume, ensuring identity is always set
        // before any conversion events fire
        setMetaUserIdentity(user.email, user.uid).catch(err =>
          console.error('[Meta Events] Failed to set user identity:', err)
        )
        
        // Clear the anonymous distinct ID from localStorage since user is now identified
        // This ensures a fresh anonymous ID is generated if they log out
        localStorage.removeItem('posthog_anonymous_distinct_id')
      } else if (!user && lastIdentifiedUserId !== null) {
        // Reset identification when user signs out
        lastIdentifiedUserId = null
        
        if (posthog) {
          posthog.reset()
        }
        
        // Also reset on native platform
        if (Capacitor.isNativePlatform()) {
          console.log('[PostHog] Native: Resetting user identification via Capacitor plugin')
          PosthogCapacitor.reset()
        }

        // Clear Meta user identity so no stale hashed data is sent after sign-out
        setMetaUserIdentity(null, null).catch(() => {})
        
        // Clear cached feature flag decision to allow fresh evaluation on next visit
        localStorage.removeItem('onboarding-feature-flag-cached')
        // Note: A new anonymous distinct ID will be generated on next page load
      }
    })

    unsubscribeRef.current = unsubscribe
    return () => {
      unsubscribe()
      unsubscribeRef.current = null
    }
  }, [posthog])

  return { user, loading }
} 